'use client'

import type React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { forwardRef, type HTMLAttributes, useEffect, useState } from 'react'

import { Skeleton } from '@/components/atomic/skeleton'
import { cn } from '@/utils/shadcn/utils'

export enum TextVariant {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  BodySubtext = 'bodySubtext',
  BodySmall = 'bodySmall',
  BodyDefault = 'bodyDefault',
  BodyLarge = 'bodyLarge',
  BodyXS = 'bodyXS',
  BodyMini = 'bodyMini',
  BodyMediumSubtext = 'bodyMediumSubtext',
}

export enum FontWeight {
  Regular = 'font-normal',
  Medium = 'font-medium',
  SemiBold = 'font-semibold',
  Bold = 'font-bold',
  ExtraBold = 'font-extrabold',
  Black = 'font-black',
}

// Leading values determine the line height for each text variant, ensuring consistent vertical spacing.
// IMPORTANT NOTE: These must be kept in sync with the variantStyles object below.
const leadingValues: Record<TextVariant, string> = {
  [TextVariant.BodyMini]: '12px',
  [TextVariant.BodyXS]: '16px',
  [TextVariant.BodySubtext]: '16px',
  [TextVariant.BodySmall]: '24px',
  [TextVariant.BodyDefault]: '24px',
  [TextVariant.BodyLarge]: '24px',
  [TextVariant.H4]: '24px',
  [TextVariant.H3]: '32px',
  [TextVariant.H2]: '32px',
  [TextVariant.H1]: '40px',
  [TextVariant.BodyMediumSubtext]: '16px',
}

export const variantStyles: Record<TextVariant, string> = {
  [TextVariant.BodyMini]: `text-[9px] leading-[12px] tracking-[0.016em] dark:tracking-[0.016em]`,
  [TextVariant.BodyXS]: `text-[11px] leading-[16px] tracking-[0.005em] dark:tracking-[0.005em]`,
  [TextVariant.BodySubtext]: `text-[12px] leading-[16px] tracking-[0em] dark:tracking-[0.005em]`,
  [TextVariant.BodySmall]: `text-[13px] leading-[24px] tracking-[-0.0025em] dark:tracking-[0.0025em]`,
  [TextVariant.BodyDefault]: `text-[14px] leading-[24px] tracking-[-0.006em] dark:tracking-[-0.001em]`,
  [TextVariant.BodyLarge]: `text-[16px] leading-[24px] tracking-[-0.011em] dark:tracking-[-0.006em]`,
  [TextVariant.H4]: `text-[16px] leading-[24px] tracking-[-0.011em] dark:tracking-[-0.006em] font-semibold`,
  [TextVariant.H3]: `text-[20px] leading-[32px] tracking-[-0.017em] dark:tracking-[-0.012em] font-semibold`,
  [TextVariant.H2]: `text-[24px] leading-[32px] tracking-[-0.019em] dark:tracking-[-0.014em] font-semibold`,
  [TextVariant.H1]: `text-[32px] leading-[40px] tracking-[-0.022em] dark:tracking-[-0.017em] font-semibold`,
  [TextVariant.BodyMediumSubtext]: `text-[12px] leading-[16px] tracking-[0em] dark:tracking-[0em]`,
}

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodyMini = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color,
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodyMini]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodyMini.displayName = 'BodyMini'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodyXS = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color,
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodyXS]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodyXS.displayName = 'BodyXS'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodySubtext = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodySubtext]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodySubtext.displayName = 'BodySubtext'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodySmall = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodySmall]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodySmall.displayName = 'BodySmall'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodyDefault = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodyDefault]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodyDefault.displayName = 'BodyDefault'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodyLarge = forwardRef<HTMLParagraphElement, TextComponentProps>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodyLarge]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodyLarge.displayName = 'BodyLarge'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const BodyMediumSubtext = forwardRef<
  HTMLParagraphElement,
  TextComponentProps
>(
  (
    {
      fontWeight = FontWeight.Regular,
      children,
      color,
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'p'
    return (
      <Comp
        className={`${variantStyles[TextVariant.BodyMediumSubtext]} ${fontWeight} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
BodyMediumSubtext.displayName = 'BodyMediumSubtext'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const H4 = forwardRef<HTMLHeadingElement, TextComponentProps>(
  (
    {
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'h4'
    return (
      <Comp
        className={`${variantStyles[TextVariant.H4]} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
H4.displayName = 'H4'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const H3 = forwardRef<HTMLHeadingElement, TextComponentProps>(
  (
    {
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'h3'
    return (
      <Comp
        className={`${variantStyles[TextVariant.H3]} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
H3.displayName = 'H3'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const H2 = forwardRef<HTMLHeadingElement, TextComponentProps>(
  (
    {
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'h2'
    return (
      <Comp
        className={`${variantStyles[TextVariant.H2]} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
H2.displayName = 'H2'

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
export const H1 = forwardRef<HTMLHeadingElement, TextComponentProps>(
  (
    {
      children,
      color = 'text-moment-teal-text',
      underline = false,
      className = '',
      testId,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'h1'
    return (
      <Comp
        className={`${variantStyles[TextVariant.H1]} ${color ?? ''} ${
          underline ? 'underline' : ''
        } ${className}`}
        ref={ref}
        {...(testId && { 'data-testid': testId })}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
H1.displayName = 'H1'

type TextComponentProps = HTMLAttributes<HTMLParagraphElement> & {
  fontWeight?: string
  children: React.ReactNode
  color?: string
  underline?: boolean
  className?: string
  testId?: string
  asChild?: boolean
}

export interface TextProps extends Partial<TextComponentProps> {
  variant: TextVariant
  text?: string
  isLoading?: boolean
  skeletonWidth?: string
  rightAlignSkeleton?: boolean
  omitWrapper?: boolean // PATCH: allow opt-in for consumers to avoid style regression
}

/**
 * @deprecated Use `Text` from `@moment/design-system-ui/text` instead.
 */
const Text = forwardRef<HTMLDivElement, TextProps>(function Text(
  {
    fontWeight = FontWeight.Regular,
    color = 'text-moment-teal-text',
    underline = false,
    className = '',
    children,
    text,
    testId,
    isLoading = false,
    variant,
    skeletonWidth = '100%',
    rightAlignSkeleton = false,
    omitWrapper = false,
    ...props
  },
  ref,
) {
  const [hideSkeleton, setHideSkeleton] = useState(!isLoading)

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (!isLoading) {
      // Hide skeleton immediately when not loading, with a small delay for smooth transition
      timeoutId = setTimeout(() => {
        setHideSkeleton(true)
      }, 200)
    } else {
      // Reset hideSkeleton when loading starts
      setHideSkeleton(false)

      // Hide skeleton after 3 seconds of loading for accessibility
      timeoutId = setTimeout(() => {
        setHideSkeleton(true)
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isLoading])

  const renderSkeleton = () => {
    const width = skeletonWidth.toString()
    const widthClass = width.endsWith('%')
      ? `w-[${width}]`
      : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
        width.includes('px')
        ? `w-[${width}]`
        : `w-[${width}px]`

    return (
      <Skeleton
        variant={
          variant === TextVariant.H1 || variant === TextVariant.H2
            ? 'heading'
            : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
              variant === TextVariant.H3 || variant === TextVariant.H4
              ? 'subheading'
              : 'default'
        }
        hidden={hideSkeleton || !isLoading}
        className={cn(
          'transition-opacity duration-300',
          widthClass,
          `h-[${leadingValues[variant]}]`,
          rightAlignSkeleton ? 'ml-auto' : '',
          className,
          isLoading ? 'opacity-100' : '!opacity-0',
          hideSkeleton ? 'hidden' : 'block',
        )}
      />
    )
  }

  const renderContent = () => {
    const content = children || text

    switch (variant) {
      case TextVariant.H1:
        return (
          <H1
            ref={ref}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </H1>
        )
      case TextVariant.H2:
        return (
          <H2
            ref={ref}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </H2>
        )
      case TextVariant.H3:
        return (
          <H3
            ref={ref}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </H3>
        )
      case TextVariant.H4:
        return (
          <H4
            ref={ref}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </H4>
        )
      case TextVariant.BodySubtext:
        return (
          <BodySubtext
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodySubtext>
        )
      case TextVariant.BodySmall:
        return (
          <BodySmall
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodySmall>
        )
      case TextVariant.BodyLarge:
        return (
          <BodyLarge
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodyLarge>
        )
      case TextVariant.BodyXS:
        return (
          <BodyXS
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodyXS>
        )
      case TextVariant.BodyMini:
        return (
          <BodyMini
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodyMini>
        )
      case TextVariant.BodyMediumSubtext:
        return (
          <BodyMediumSubtext
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodyMediumSubtext>
        )
      // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
      case TextVariant.BodyDefault:
      default:
        return (
          <BodyDefault
            ref={ref}
            fontWeight={fontWeight}
            color={color}
            underline={underline}
            className={className}
            testId={testId}
            {...props}
          >
            {content}
          </BodyDefault>
        )
    }
  }

  if (omitWrapper && isLoading) return renderSkeleton()
  if (omitWrapper) return renderContent()

  return (
    <div className="relative min-w-16">
      <div className="absolute top-0 left-0 size-full">{renderSkeleton()}</div>
      <div className="min-h-4">{renderContent()}</div>
    </div>
  )
}) as React.FunctionComponent<TextProps & React.RefAttributes<HTMLElement>>

Text.displayName = 'Text'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default Text
